import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Clock, MapPin, Trophy, Presentation, GraduationCap, Play } from 'lucide-react';
import { API_URL, formatCountdown } from '@/lib/api';

const TYPE_ICONS = {
  demonstration: Play,
  seminar: Presentation,
  workshop: GraduationCap,
  competition: Trophy,
};

const TYPE_COLORS = {
  demonstration: 'from-blue-500 to-cyan-500',
  seminar: 'from-purple-500 to-pink-500',
  workshop: 'from-emerald-500 to-teal-500',
  competition: 'from-orange-500 to-red-500',
};

function CountdownTimer({ target }) {
  const [time, setTime] = useState(formatCountdown(target));
  
  useEffect(() => {
    const t = setInterval(() => setTime(formatCountdown(target)), 1000);
    return () => clearInterval(t);
  }, [target]);

  if (time.expired) {
    return <div className="text-red-400 font-bold text-xs">EXPIRED</div>;
  }

  return (
    <div className="flex items-center gap-2" data-testid="countdown-timer">
      {[
        { v: time.days, l: 'D' },
        { v: time.hours, l: 'H' },
        { v: time.minutes, l: 'M' },
        { v: time.seconds, l: 'S' },
      ].map((item) => (
        <div key={item.l} className="bg-slate-800/60 backdrop-blur rounded px-2 py-1 min-w-[38px] text-center border border-slate-700">
          <div className="text-white font-black text-sm leading-none">{String(item.v).padStart(2, '0')}</div>
          <div className="text-slate-500 text-[9px] mt-0.5">{item.l}</div>
        </div>
      ))}
    </div>
  );
}

export default function EventSchedulePage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/events`);
        setEvents(res.data.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredEvents = filter === 'all' ? events : events.filter(e => e.type === filter);

  // Group by day
  const grouped = filteredEvents.reduce((acc, ev) => {
    const day = new Date(ev.start_time).toDateString();
    if (!acc[day]) acc[day] = [];
    acc[day].push(ev);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-950">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/80 backdrop-blur-xl border-b border-emerald-500/20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <div className="text-emerald-400 text-xs font-bold uppercase tracking-wider">Program Acara</div>
            <h1 className="text-white text-2xl font-black">Event Schedule</h1>
          </div>
          <div className="hidden md:block text-right">
            <div className="text-emerald-300 text-xs">Hari Landskap Negara 2026</div>
            <div className="text-slate-400 text-[10px]">Stadium Hang Jebat, Melaka</div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['all', 'demonstration', 'seminar', 'workshop', 'competition'].map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase transition-all ${
                filter === t
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                  : 'bg-slate-800/70 text-slate-300 hover:bg-slate-800 border border-slate-700'
              }`}
              data-testid={`filter-type-${t}`}
            >
              {t === 'all' ? 'All Events' : t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading events...</div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="text-center py-12 text-slate-400">No events found</div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([day, dayEvents]) => (
              <div key={day}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-emerald-400 text-xs font-bold uppercase">{new Date(day).toLocaleDateString('en', { weekday: 'long' })}</div>
                    <div className="text-white text-lg font-bold">{new Date(day).toLocaleDateString('en', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                  </div>
                </div>

                <div className="space-y-3 ml-3 pl-6 border-l border-emerald-500/30">
                  {dayEvents.map((ev) => {
                    const Icon = TYPE_ICONS[ev.type] || Calendar;
                    const color = TYPE_COLORS[ev.type] || 'from-slate-500 to-slate-600';
                    return (
                      <div key={ev.id} className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-5 border border-slate-700/50 hover:border-emerald-500/50 transition-colors relative" data-testid={`event-${ev.id}`}>
                        <div className={`absolute -left-[38px] top-6 w-6 h-6 rounded-full bg-gradient-to-br ${color} flex items-center justify-center shadow-lg`}>
                          <Icon className="w-3 h-3 text-white" />
                        </div>
                        
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className={`px-2 py-0.5 rounded-full bg-gradient-to-r ${color} text-white text-[10px] font-black uppercase`}>{ev.type}</span>
                              {ev.is_featured && <span className="px-2 py-0.5 rounded-full bg-yellow-500 text-black text-[10px] font-black">FEATURED</span>}
                            </div>
                            <h3 className="text-white font-bold text-base mb-1">{ev.title}</h3>
                            {ev.description && <p className="text-slate-400 text-sm mb-2">{ev.description}</p>}
                            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />
                                {new Date(ev.start_time).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })} - 
                                {new Date(ev.end_time).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              {ev.booth_id && (
                                <Link to={`/booth/${ev.booth_id}`} className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300">
                                  <MapPin className="w-3 h-3" /> View booth
                                </Link>
                              )}
                            </div>
                          </div>
                          
                          <div className="md:text-right">
                            <div className="text-slate-500 text-[10px] uppercase mb-1">Starts in</div>
                            <CountdownTimer target={ev.start_time} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
